/**
 * The Targets card on Location Details (replaces the old Target Achievement
 * bar): every target of the selected location in maturity order, with its
 * strength, the maturity it needs and its status, e.g. "Reached 22 Sep 2026,
 * 05:25 AEST (11h 50m after concrete start)" or "Not yet: 120.3 °C·hrs to
 * go". There is no time-to-target prediction, only the maturity still to go.
 * Targets flagged outside the calibration's tested range say so. Phones get
 * one stacked block per target instead of the table.
 */
import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import type { CalibratedRange, CalibrationCheck, ChartTarget, MaturityDescriptor } from '../../services/pours';
import type { IsoLike } from '../../utils/datetime';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  formatHalfUp,
  formatMaturity,
  isTargetOutsideRange,
  maturityShortLabel,
  targetStrengthText,
  valueWithUnit,
} from '../../utils/maturityDisplay';
import { targetStatusText, type TargetStatus } from '../../utils/targetStatus';

/** Tooltip on the "-" of a target without a strength. */
export const MATURITY_ONLY_TOOLTIP = 'Set by maturity only';
/** Tooltip on a target flagged outside the tested range. */
export const TARGET_OUTSIDE_TOOLTIP =
  'Outside the tested strength-maturity range, so the strength at this target is a trend estimate.';
/** ASTM C1074 9.2: the pour's maturity basis does not match its calibration's. */
export const MISMATCH_CAPTION =
  'Strength is not shown for this pour: its calibration uses a different maturity method (ASTM C1074 9.2). ' +
  'Targets are still tracked by maturity. A target set by strength had its maturity worked out from that ' +
  'calibration, so confirm it with test results.';
/** A curve without included samples (NCS*). */
export const NO_SAMPLES_CAPTION =
  'The calibration has no included samples (NCS*), so strength is not estimated. Targets are tracked by maturity.';

export interface TargetsCardProps {
  /** The location's targets (the chart response's `target`). */
  targets: ReadonlyArray<ChartTarget>;
  /** The pour's maturity naming. */
  meta: MaturityDescriptor;
  /** The company strength unit. */
  strengthUnit?: string | null;
  /** The calibration's tested range, to flag targets on responses that do
   *  not send outside_range, and for the grade note. */
  calibratedRange?: CalibratedRange | null;
  /** The location's current maturity; missing counts as 0. */
  currentMaturity?: number | null;
  /** The location's concrete start (maturity counted from), naive UTC. */
  concreteStart?: IsoLike;
  /** The location's monitoring start, used when there is no concrete start. */
  monitoringStart?: IsoLike;
  /** Monitoring has ended (pour completed or the location's logging ended). */
  ended?: boolean;
  /** The pour's IANA timezone; UTC when missing. */
  timezoneIana?: string | null;
  /** The pour against its calibration (ASTM C1074 9.2); 'mismatch' shows no
   *  strength anywhere. */
  calibrationCheckStatus?: CalibrationCheck['status'] | null;
  /** The mix design's grade strength (company unit). */
  gradeStrength?: number | null;
  /** The grade strength lies outside the tested range. */
  gradeOutsideRange?: boolean;
}

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** One target as the card shows it. */
interface TargetRow {
  target: ChartTarget;
  name: string;
  isFinal: boolean;
  /** "10.0 MPa", or '' for a maturity-only target. */
  strength: string;
  needed: string;
  outside: boolean;
  status: TargetStatus;
  /** The next target to reach: its status is bold. */
  next: boolean;
}

/** "2 of 4 reached", or "All 4 reached". */
const summaryText = (reached: number, total: number): string =>
  reached === total && total > 1 ? `All ${total} reached` : `${reached} of ${total} reached`;

/** The note under the table when the grade strength is outside the tested
 *  range: "This calibration covers up to 14.2 MPa, so the 80.0 MPa grade
 *  strength is not estimated." Null when it does not apply. */
const gradeNote = (
  grade: number | null | undefined,
  outside: boolean | undefined,
  range: CalibratedRange | null | undefined,
  unit: string | null | undefined,
): string | null => {
  const g = finite(grade);
  if (!outside || g == null || !(g > 0) || !range?.has_curve || !range.has_samples) return null;
  const sMin = finite(range.s_min);
  const sMax = finite(range.s_max);
  const gradeText = valueWithUnit(formatHalfUp(g), unit);
  if (sMax != null && g > sMax) {
    return `This calibration covers up to ${valueWithUnit(formatHalfUp(sMax), unit)}, so the ${gradeText} grade strength is not estimated.`;
  }
  if (sMin != null && g < sMin) {
    return `This calibration covers from ${valueWithUnit(formatHalfUp(sMin), unit)}, so the ${gradeText} grade strength is not estimated.`;
  }
  return null;
};

const FinalChip: React.FC = () => (
  <Chip
    label="Final"
    size="small"
    variant="outlined"
    sx={{ ml: 0.75, height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.6 } }}
  />
);

/** The strength value: "-" (set by maturity only) without one, and the amber
 *  outside-range flag under it. */
const StrengthCell: React.FC<{ row: TargetRow }> = ({ row }) => (
  <>
    {row.strength ? (
      <span>{row.strength}</span>
    ) : (
      <Tooltip title={MATURITY_ONLY_TOOLTIP} placement="top" arrow>
        <span style={{ cursor: 'help' }}>-</span>
      </Tooltip>
    )}
    {row.outside && (
      <Tooltip title={TARGET_OUTSIDE_TOOLTIP} placement="top" arrow>
        <Box
          data-testid="target-outside-range"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, cursor: 'help', width: 'fit-content' }}
        >
          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'warning.dark', lineHeight: 1.3 }}>
            {TARGET_OUTSIDE_RANGE_LABEL}
          </Typography>
        </Box>
      </Tooltip>
    )}
  </>
);

/** Green check for a reached target, grey clock otherwise (decorative: the
 *  status line says it), then the status line. */
const StatusLine: React.FC<{ row: TargetRow }> = ({ row }) => {
  const reached = row.status.kind === 'reached';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
      {reached ? (
        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px', flexShrink: 0 }} />
      ) : (
        <ScheduleIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
      )}
      <Typography variant="body2" component="span" sx={{ fontSize: '0.8rem', fontWeight: row.next ? 700 : 400 }}>
        {row.status.text}
      </Typography>
    </Box>
  );
};

export const TargetsCard: React.FC<TargetsCardProps> = ({
  targets,
  meta,
  strengthUnit,
  calibratedRange,
  currentMaturity,
  concreteStart,
  monitoringStart,
  ended,
  timezoneIana,
  calibrationCheckStatus,
  gradeStrength,
  gradeOutsideRange,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const sorted = [...targets].sort(
    (a, b) => (a.target_ea ?? 0) - (b.target_ea ?? 0) || (a.target_strength ?? 0) - (b.target_strength ?? 0),
  );
  // Older responses do not flag the final target: it is the highest maturity.
  const flagsFinal = sorted.some((t) => t.is_final != null);
  let nextFound = false;
  const rows: TargetRow[] = sorted.map((t, i) => {
    const status = targetStatusText({
      achieved: t.achieved,
      achievedAt: t.achievement_time,
      targetMaturity: t.target_ea,
      currentMaturity,
      concreteStart,
      monitoringStart,
      ended,
      meta,
      timezoneIana,
    });
    const next = !nextFound && (status.kind === 'pending' || status.kind === 'passed');
    if (next) nextFound = true;
    return {
      target: t,
      name: t.target_label?.trim() || 'Target',
      isFinal: flagsFinal ? !!t.is_final : i === sorted.length - 1,
      strength: targetStrengthText(t.target_strength, strengthUnit),
      needed: finite(t.target_ea) != null && t.target_ea > 0 ? formatMaturity(t.target_ea, meta) : '-',
      outside: isTargetOutsideRange(t, calibratedRange),
      status,
      next,
    };
  });
  const reached = rows.filter((r) => r.target.achieved).length;
  const neededLabel = `${maturityShortLabel(meta)} needed`;

  // Why strength is missing: a 9.2 mismatch, or a curve without samples.
  const caption =
    calibrationCheckStatus === 'mismatch'
      ? MISMATCH_CAPTION
      : calibratedRange?.has_curve && !calibratedRange.has_samples
        ? NO_SAMPLES_CAPTION
        : null;
  const note = gradeNote(gradeStrength, gradeOutsideRange, calibratedRange, strengthUnit);

  const nameNode = (row: TargetRow) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{row.name}</span>
      {row.isFinal && <FinalChip />}
    </Box>
  );

  return (
    <Paper component="section" aria-label="Targets" elevation={1} sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" component="h3" fontWeight={700}>
          Targets
        </Typography>
        <Typography variant="caption" color="text.secondary" data-testid="targets-summary">
          {summaryText(reached, rows.length)}
        </Typography>
      </Box>

      {isPhone ? (
        <Box component="ul" aria-label="Targets" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {rows.map((row) => (
            <Box
              component="li"
              key={row.target.target_id}
              data-outside-range={row.outside ? 'true' : undefined}
              sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider', fontSize: '0.8rem' }}
            >
              <Typography variant="body2" fontWeight={600} component="div">
                {nameNode(row)}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Strength</Typography>
                <Box sx={{ fontSize: '0.8rem' }}><StrengthCell row={row} /></Box>
                <Typography variant="caption" color="text.secondary">{neededLabel}</Typography>
                <Box sx={{ fontSize: '0.8rem' }}>{row.needed}</Box>
              </Box>
              <Box sx={{ mt: 0.5 }}>
                <StatusLine row={row} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            aria-label="Targets"
            sx={{ '& th, & td': { fontSize: '0.8rem', py: 0.75, px: 1, verticalAlign: 'top' } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Strength</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>{neededLabel}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.target.target_id} data-outside-range={row.outside ? 'true' : undefined}>
                  <TableCell>{nameNode(row)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><StrengthCell row={row} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.needed}</TableCell>
                  <TableCell><StatusLine row={row} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-testid="targets-caption"
          sx={{ mt: 1, lineHeight: 1.4 }}
        >
          {caption}
        </Typography>
      )}
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-testid="targets-grade-note"
          sx={{ mt: 1, lineHeight: 1.4 }}
        >
          {note}
        </Typography>
      )}
    </Paper>
  );
};
